import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link, Tooltip, Table, TableRow, TableBody, TableCell } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { CollapseCard, IconButton } from '@totalsoft/rocket-ui';
import { DocumentVerificationStatus } from 'constants/defaultValues';
import VerificationsSummary from './VerificationsSummary';
import { useAuthorization } from 'hooks';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { BoldTableHead } from 'components/common/TableStyles';
import { VerificationsTableWrapper } from './VerificationStyles';

const VerificationsComponent = ({ verifications, onOpenLink, onRetryVerification }) => {
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditChecks = useAuthorization(AuthorizationCodes.hasRightToEditChecks, documentTypeId, documentStateId);
  const { t } = useTranslation();

  return (
    <CollapseCard
      defaultExpanded
      icon={DoneAllIcon}
      title={t('Tooltips.Verifications')}
      header={<VerificationsSummary verifications={verifications} />}
      toggleOnHeaderClick
    >
      <VerificationsTableWrapper container spacing={2}>
        <Table>
          <BoldTableHead>
            <TableRow>
              <TableCell>{t('Offer.Edit.Verifications.Type')}</TableCell>
              <TableCell>{t('Offer.Edit.Verifications.Partner')}</TableCell>
              <TableCell>{t('Offer.Edit.Verifications.State')}</TableCell>
              <TableCell>{t('Offer.Edit.Verifications.User')}</TableCell>
              <TableCell>{t('Offer.Edit.Verifications.Date')}</TableCell>
              <TableCell>{t('Offer.Edit.Verifications.ValidityDate')}</TableCell>
              <TableCell>{t('Offer.Edit.Verifications.SummaryTitle')}</TableCell>
              <TableCell>{t('Offer.Edit.Verifications.Comments')}</TableCell>
              <TableCell>{t('Tooltips.Retry')}</TableCell>
            </TableRow>
          </BoldTableHead>
          <TableBody>
            {verifications &&
              verifications.map((verification, index) => (
                <TableRow key={index}>
                  <TableCell>{verification.verificationName}</TableCell>
                  <TableCell>{verification.entityName}</TableCell>
                  <TableCell>{verification.statusName}</TableCell>
                  <TableCell>{verification.userName}</TableCell>
                  <TableCell>
                    {t('DATE_FORMAT', {
                      date: { value: verification.verificationDate, format: 'DD/MM/YY HH:mm:ss' }
                    })}
                    &nbsp;
                  </TableCell>
                  <TableCell>
                    {t('DATE_FORMAT', { date: { value: verification.validityDate, format: 'DD/MM/YY HH:mm:ss' } })}
                    &nbsp;
                  </TableCell>
                  <TableCell>
                    {verification.summaryTitle ? (
                      <Link color='primary' underline='hover' onClick={() => onOpenLink(verification)}>
                        {verification.summaryTitle}
                      </Link>
                    ) : (
                      <span>&nbsp;</span>
                    )}
                  </TableCell>
                  <TableCell>{verification.message}</TableCell>
                  <Tooltip
                    title={
                      !verification.verificationTypeIsActive
                        ? t('Offer.Edit.Verifications.VerificationTypeInactive')
                        : verification.statusCode === DocumentVerificationStatus.IN_PROGRESS
                        ? t('Offer.Edit.Verifications.VerificationRunning')
                        : t('Tooltips.Retry')
                    }
                    aria-label='info'
                    placement='right'
                  >
                    <TableCell>
                      <IconButton
                        id={'Retry_Verification_' + index}
                        disabled={
                          globalReadOnly({
                            controlName: 'Verifications',
                            hasAuthorization: hasRightToEditChecks
                          }) ||
                          verification.statusCode === DocumentVerificationStatus.IN_PROGRESS ||
                          !verification.verificationTypeIsActive
                        }
                        color='primary'
                        onClick={() => onRetryVerification(verification.documentVerificationId)}
                      >
                        <ReplayIcon />
                      </IconButton>
                    </TableCell>
                  </Tooltip>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </VerificationsTableWrapper>
    </CollapseCard>
  );
};

VerificationsComponent.propTypes = {
  verifications: PropTypes.array,
  onOpenLink: PropTypes.func,
  onRetryVerification: PropTypes.func.isRequired
};

export default VerificationsComponent;
